import { Spin, Typography } from "antd-v5";
import { isNull } from "lodash";
import React, { useCallback, useEffect, useState } from "react";
import InsightsStatsAPI from "restAPI/InsightsAPI";
import { FindingStatusStat } from "types/api/findingStatusStat";
import { capitalize, find } from "utils/lodashUtils";
import FeedsAPI from "views/Feeds/FeedsAPI";

const DISPLAY_DESCRIPTION_FIELDS = ["cve", "cwesStrings", "title"];

const DISPLAY_TITLE: { [key: string]: string } = {
  cwesStrings: "CWE",
  title: "Summary",
  cve: "CVE",
};

export default function GroupByDescription({
  groupByField,
  record,
}: {
  groupByField: string;
  record: FindingStatusStat;
}) {
  const [description, setDescription] = useState<
    string | JSX.Element | undefined
  >();
  const [loading, setLoading] = useState(true);
  const [groupByKey, setGroupByKey] = useState(groupByField);
  const [recordState, setRecordState] = useState<FindingStatusStat>(record);
  const displayTitle = DISPLAY_TITLE[groupByKey] ?? capitalize(groupByKey);

  const fetchCWEDescription = useCallback(() => {
    let api = InsightsStatsAPI.getCWECVE({
      filters: {
        cwes:
          recordState.name === "null" || recordState.name === null
            ? [null]
            : [recordState.name?.replace("CWE-", "")],
      },

      aggFields: ["cwes", "severity", "source"],
      page: 0,
      size: 10,
    });
    const { promise, cancel } = api;
    promise
      .then(({ stats }) => {
        const descriptionObj = find(
          stats,
          (response) =>
            response["name"] === recordState.name?.replace("CWE-", ""),
        );
        setDescription(descriptionObj?.["desc"]);
        return cancel;
      })
      .finally(() => setLoading(false));
  }, [recordState.name]);

  const fetchCVEDescription = useCallback(() => {
    //Handling CVE Description for No CVE
    if (recordState.name === "null" || isNull(recordState.name)) {
      setDescription(<i>No {displayTitle} description available</i>);
      setLoading(false);
      return;
    }
    if (recordState.name) {
      const searchParams = new URLSearchParams({
        searchText: recordState.name,
      }).toString();
      let api = FeedsAPI.getFeeds(searchParams);
      const { promise, cancel } = api;
      promise
        .then(({ content }) => {
          const descriptionObj = find(
            content,
            (response) => response["feedId"] === recordState.name,
          );

          setDescription(
            descriptionObj?.["description"] ? (
              descriptionObj["description"]
            ) : (
              <i>No {displayTitle} description available</i>
            ),
          );
          return cancel;
        })
        .finally(() => setLoading(false));
    }
  }, [recordState.name, displayTitle]);

  useEffect(() => {
    setGroupByKey(groupByField);
    setRecordState({});
  }, [groupByField, groupByKey]);

  useEffect(() => {
    setRecordState(record);
  }, [record]);

  useEffect(() => {
    if (!recordState.name) {
      return;
    }
    if (groupByKey === "title") {
      setLoading(false);
      setDescription(recordState.name);
    } else if (groupByKey === "cve") {
      fetchCVEDescription();
    } else if (groupByKey === "cwesStrings") {
      fetchCWEDescription();
    }
  }, [groupByKey, recordState.name, fetchCVEDescription, fetchCWEDescription]);

  if (!DISPLAY_DESCRIPTION_FIELDS.includes(groupByKey)) {
    return null;
  }

  return (
    <Spin spinning={loading}>
      <Typography.Paragraph type="secondary" className="m-0">
        {displayTitle} {groupByKey === "title" ? "" : "Description"} :{" "}
        {description}
      </Typography.Paragraph>
    </Spin>
  );
}
